import classNames from 'classnames';
import { ComponentProps, type FocusEventHandler, HTMLInputTypeAttribute, useEffect, useRef } from 'react';
import type Typography from '@alltrails/core/components/Typography';
import { InputBaseProps, Size } from '../../types';
import setDenaliAttribute from '../../utils/setDenaliAttribute';
import IconRenderer, { IconDefinition } from '../IconRenderer';
import { IconRendererProps } from '../IconRenderer/IconRenderer';
import InputField from '../InputField';
import getErrorMessage from './getErrorMessage';
import styles from './styles/styles.module.scss';

type TextInputProps = InputBaseProps & {
  autoComplete?: string; // https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill
  autoFocus?: boolean;
  characterLimit?: number;
  className?: string;
  inputMode?: 'decimal' | 'email' | 'numeric' | 'tel' | 'text';
  isFocused?: boolean;
  labelTextColor?: ComponentProps<typeof Typography>['color'];
  leadingIcon?: IconDefinition<'orientation'>;
  name?: string;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onChange: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  recurlyDataId?: string;
  required?: boolean;
  trailingIcon?: IconDefinition<'orientation'>;
  // When provided, the trailing icon is wrapped in a button for clickable affordances
  // like a password show/hide toggle. Requires a `trailingIconTitle` for accessibility.
  onTrailingIconClick?: () => void;
  trailingIconTitle?: string;
  type?: HTMLInputTypeAttribute;
  value?: string;
  size?: Size<'sm' | 'md'>;
};

const TextInput = ({
  autoComplete,
  autoFocus,
  className,
  disabled,
  errorText,
  helperText,
  hideLabel,
  inputMode,
  labelText,
  labelTextColor,
  labelTextVariant,
  leadingIcon,
  name,
  onBlur,
  onChange,
  onKeyDown,
  placeholder,
  recurlyDataId,
  required = false,
  trailingIcon,
  onTrailingIconClick,
  trailingIconTitle,
  type = 'text',
  value,
  isFocused,
  characterLimit,
  size = 'md'
}: TextInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const iconDefaults: IconRendererProps['defaults'] = { color: disabled ? '--color-text-disabled' : '--color-text-secondary', size: 'md' };

  useEffect(() => {
    if (isFocused && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isFocused]);

  const errorMessage = getErrorMessage(errorText, value, required, characterLimit);

  return (
    <InputField
      {...setDenaliAttribute('TextInput')}
      className={className}
      disabled={disabled}
      errorText={errorMessage}
      helperText={helperText}
      labelText={hideLabel ? '' : labelText}
      labelTextColor={labelTextColor}
      labelTextVariant={labelTextVariant}
      characterLimitText={value && characterLimit ? `${value.length} / ${characterLimit}` : undefined}
      size={size}
    >
      <div className={classNames(styles.inputContainer, styles[size], { [styles.disabled]: disabled, [styles.hasError]: Boolean(errorMessage) })}>
        {leadingIcon ? <IconRenderer icon={leadingIcon} defaults={iconDefaults} /> : null}
        <input
          ref={inputRef}
          aria-label={hideLabel ? labelText : undefined}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className={classNames(styles.input, styles[size], { [styles.hasLeadingIcon]: !!leadingIcon, [styles.hasTrailingIcon]: !!trailingIcon })}
          disabled={disabled}
          inputMode={inputMode}
          name={name}
          onBlur={onBlur}
          onChange={e => {
            onChange(e.target.value);
          }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          required={required}
          type={type}
          value={value}
          data-recurly={recurlyDataId}
        />
        {trailingIcon ? (
          onTrailingIconClick ? (
            <button
              type="button"
              className={styles.trailingIconButton}
              onClick={e => {
                // Prevent the parent <label> from forwarding focus to the input on toggle clicks.
                e.preventDefault();
                onTrailingIconClick();
              }}
              title={trailingIconTitle}
              aria-label={trailingIconTitle}
            >
              <IconRenderer icon={trailingIcon} defaults={iconDefaults} />
            </button>
          ) : (
            <IconRenderer icon={trailingIcon} defaults={iconDefaults} />
          )
        ) : null}
      </div>
    </InputField>
  );
};

export default TextInput;
